import { IScheduleDetail, ScheduleKeys } from 'list/international/types';
import { IKeyStringValue } from 'common/types';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.locale('ko');
dayjs.extend(customParseFormat);

const TIME_START_INDEX = 8;
const TIME_END_INDEX = 12;
const MINUTES_FOR_22_HOUR = 22 * 60;
const MINUTES_FOR_06_HOUR = 6 * 60;
const MINIMUM_WAITING_TIME_IN_MINUTES = 4 * 60;

const getHHMMFromDetail = (timeString: string) =>
  timeString.slice(TIME_START_INDEX, TIME_END_INDEX);

const getMinutesFromHHMM = (hhmm: string) => {
  const h = +hhmm.slice(0, 2);
  const m = +hhmm.slice(2, 4);
  return h * 60 + m;
};

export const getHasNightTransfer = (schedules: IScheduleDetail) => {
  const waitingMinutes = getMinutesFromHHMM(
    schedules[ScheduleKeys.waitingTime],
  );
  const arrivalHHMM = getHHMMFromDetail(schedules[ScheduleKeys.endDateTime]);
  const arrivalMinutes = getMinutesFromHHMM(arrivalHHMM);

  let tempWaitingMinutes = waitingMinutes;
  let tempArrivalMinutes = arrivalMinutes;
  let validWaitingMinutes = 0;

  while (tempWaitingMinutes) {
    if (tempArrivalMinutes < MINUTES_FOR_06_HOUR) {
      const diffMinutes = MINUTES_FOR_06_HOUR - tempArrivalMinutes;
      if (diffMinutes <= tempWaitingMinutes) {
        validWaitingMinutes += diffMinutes;
        tempArrivalMinutes += diffMinutes;
        tempWaitingMinutes -= diffMinutes;
        // 0600시로 만듬, 그만큼 유효한 waiting Time 적립
      } else {
        validWaitingMinutes += tempWaitingMinutes;
        break;
      }
    } else if (tempArrivalMinutes < MINUTES_FOR_22_HOUR) {
      const diffMinutes = MINUTES_FOR_22_HOUR - tempArrivalMinutes;
      if (diffMinutes <= tempWaitingMinutes) {
        // validWaitingMinutes 에는 변화 없음
        tempArrivalMinutes += diffMinutes;
        tempWaitingMinutes -= diffMinutes;
      } else {
        break;
      }
    } else {
      const diffMinutes = 24 * 60 - tempArrivalMinutes;
      if (diffMinutes <= tempWaitingMinutes) {
        validWaitingMinutes += diffMinutes;
        tempArrivalMinutes += diffMinutes;
        tempArrivalMinutes -= 24 * 60; // 24시간 만큼 줄임
        tempWaitingMinutes -= diffMinutes;
        // 24시로 만듬, 그만큼 유효한 waitingTime 적립
      } else {
        validWaitingMinutes += tempWaitingMinutes;
        break;
      }
    }
  }
  return validWaitingMinutes >= MINIMUM_WAITING_TIME_IN_MINUTES;
};

export const getScheduleTitle = ({
  airportCode,
  flightDateTime,
  airports,
}: {
  airportCode: string;
  flightDateTime: string;
  airports: IKeyStringValue;
}) => {
  const cityName = airports[airportCode] || '';

  // `${공항코드} ${도시이름} ${MM.DD} (${요일}) ${HH:mm}` <- 도시 이름 없을 시, 도시 이름 생략
  return `${airportCode}${cityName ? ' ' + cityName : ''} ${dayjs(flightDateTime, 'YYYYMMDDHHmm').format('MM.DD (ddd) HH:mm')}`;
};

export const getIsDepartureAtNight = (schedule: IScheduleDetail) => {
  const departureHHMM = getHHMMFromDetail(schedule[ScheduleKeys.startDateTime]);
  const departureMinutes = getMinutesFromHHMM(departureHHMM);
  return (
    departureMinutes >= MINUTES_FOR_22_HOUR ||
    departureMinutes <= MINUTES_FOR_06_HOUR
  );
};

export const getIsArrivalAtNight = (schedule: IScheduleDetail) => {
  const arrivalHHMM = getHHMMFromDetail(schedule[ScheduleKeys.endDateTime]);
  const arrivalMinutes = getMinutesFromHHMM(arrivalHHMM);
  return (
    arrivalMinutes >= MINUTES_FOR_22_HOUR ||
    arrivalMinutes <= MINUTES_FOR_06_HOUR
  );
};

export const getDifferentNextAirport = (
  schedule: IScheduleDetail[],
  index: number,
) => {
  if (!schedule[index + 1]) return false;
  return (
    schedule[index][ScheduleKeys.endAirport] !==
    schedule[index + 1][ScheduleKeys.startAirport]
  );
};

export const getIsLongWaitingTime = (schedule: IScheduleDetail) => {
  const waitingMinutes = getMinutesFromHHMM(schedule[ScheduleKeys.waitingTime]);
  return MINIMUM_WAITING_TIME_IN_MINUTES <= waitingMinutes;
};
